import { isStr, trimQuotePairNoWs } from '../utils/common';
import { rxHead, rxsHtmlAttrCapture, rxsHtmlAttrs } from './regex';

// From https://stackoverflow.com/questions/3458217/how-to-use-regular-expression-to-match-the-charset-string-in-html
// const rxContentCharset = /<meta(?!\s*(?:name|value)\s*=)(?:[^>]*?content\s*=[\s"']*)?([^>]*?)[\s"';]*charset\s*=[\s"']*([^\s"'/>]*)/i;

const rxsMeta = String.raw`(<meta)(${rxsHtmlAttrs})(\s*\/?>)`;

// This is looking for charset inside of the "content" HTMl attr
const rxCharsetInContentType = /(charset[ \t\v\f]*=[ \t\v\f]*['"]?)([\w-]*)(['"]?)/i;

export function getHtmlMetaCharset(html: string | Buffer): string | undefined {
	const htmlStr = isStr(html) ? html : html.toString('latin1');

	const rx = new RegExp(rxsMeta, 'gi');

	while (true) {
		const match1 = rx.exec(htmlStr);

		if (match1 === null) {
			break;
		}

		const [, , metaAttrsStr] = match1;

		// Now go through the attrs looking for a modern "charset" attr, or an http-equiv & content attrs
		let isContentTypeMeta = false;
		let contentTypeCharset: string | undefined;

		const rxAttr = new RegExp(rxsHtmlAttrCapture, 'gi');

		while (true) {
			const match2 = rxAttr.exec(metaAttrsStr);

			if (match2 === null) {
				break;
			}

			const attr = match2[1];
			const value: string | undefined = match2[2];

			if (typeof value !== 'string') {
				break;
			}

			const attr2 = attr.toLowerCase();
			const value2 = trimQuotePairNoWs(value).trim();

			if (attr2 === 'charset') {
				// We have an explicit charset attr... is charset meta
				return value2;
			}

			const value3 = value2.toLowerCase();
			if (attr2 === 'http-equiv' && value3 === 'content-type') {
				isContentTypeMeta = true;
			}

			if (attr2 === 'content' && value3.includes('charset')) {
				const match3 = rxCharsetInContentType.exec(value);
				if (match3) {
					contentTypeCharset = match3[2];
				}
			}

			if (isContentTypeMeta && contentTypeCharset) {
				return contentTypeCharset;
			}
		}
	}
}

export function removeHtmlMetaCharset(html: string): string {
	const rx = new RegExp(rxsMeta, 'gi');
	return html.replace(rx, (metaTagStr, _pre, metaAttrsStr) => {
		if (!metaAttrsStr) {
			// Empty <meta>
			return metaTagStr;
		}

		// Now go through the attrs looking for a modern "charset" attr, or an http-equiv & content attrs
		let isContentTypeMeta = false;
		let hasCharsetContent = false;

		const rxAttr = new RegExp(rxsHtmlAttrCapture, 'gi');

		while (true) {
			const match2 = rxAttr.exec(metaAttrsStr);

			if (match2 === null) {
				break;
			}

			const attr = match2[1];
			const value: string | undefined = match2[2];

			const attr2 = attr.toLowerCase();
			if (typeof value === 'string' && attr2 === 'charset') {
				// We have an explicit charset attr... is charset meta
				return '';
			}

			if (value) {
				const value2 = trimQuotePairNoWs(value).trim().toLowerCase();

				if (attr2 === 'http-equiv' && value2 === 'content-type') {
					isContentTypeMeta = true;
				} else if (attr2 === 'content' && value2.includes('charset')) {
					hasCharsetContent = true;
				}

				if (isContentTypeMeta && hasCharsetContent) {
					return '';
				}
			}
		}

		// No "charset" attr or "content" attr found... return as-is
		return metaTagStr;
	});
}

export function replaceHtmlMetaCharset(
	html: string,
	charset: string
): {
	html: string;
	replaced: boolean;
	previous: string | undefined;
} {
	const rx = new RegExp(rxsMeta, 'gi');

	let replaced = false;
	let current: string | undefined;

	const html2 = html.replace(rx, (metaTagStr, metaStartStr, metaAttrsStr, metaEndStr) => {
		if (!metaAttrsStr) {
			// Empty <meta>
			return metaTagStr;
		}

		// Now go through the attrs looking for a modern "charset" attr, or an http-equiv & content attrs
		let isContentTypeMeta = false;
		let hasCharsetContentFirst = true;

		const rxAttr = new RegExp(rxsHtmlAttrCapture, 'gi');

		let metaAttrsStr2 = metaAttrsStr.replace(
			rxAttr,
			(attrStr: string, attr: string, value: string | undefined) => {
				if (typeof value === 'string') {
					const attr2 = attr.toLowerCase();
					const value2 = trimQuotePairNoWs(value).trim();

					if (attr2 === 'charset') {
						// We have an explicit charset attr... is charset meta
						replaced = true;
						current = value2;
						return ' ' + attr + `="${charset}"`;
					}

					const value3 = value2.toLowerCase();
					if (attr2 === 'http-equiv' && value3 === 'content-type') {
						isContentTypeMeta = true;
						return attrStr;
					}

					if (attr2 === 'content' && value3.includes('charset')) {
						if (isContentTypeMeta) {
							const value4 = value.replace(
								rxCharsetInContentType,
								(_, pre, currentCharset, post) => {
									replaced = true;
									current = currentCharset;
									return pre + charset + post;
								}
							);
							return ' ' + attr + '=' + value4;
						} else {
							// In case the content attr comes before the http-equiv attr... replace later
							hasCharsetContentFirst = true;
						}
					}
				}

				// Some other meta
				return attrStr;
			}
		);

		// If content attr comes first, we'll have to loop again to replace it
		if (isContentTypeMeta && hasCharsetContentFirst) {
			metaAttrsStr2 = metaAttrsStr2.replace(
				rxAttr,
				(attrStr: string, attr: string, value: string | undefined) => {
					if (typeof value === 'string') {
						const attr2 = attr.toLowerCase();
						const value2 = trimQuotePairNoWs(value).trim();
						const value3 = value2.toLowerCase();

						if (attr2 === 'content' && value3.includes('charset')) {
							const value4 = value.replace(
								rxCharsetInContentType,
								(_, pre, currentCharset, post) => {
									replaced = true;
									current = currentCharset;
									return pre + charset + post;
								}
							);
							return ' ' + attr + '=' + value4;
						}
					}

					// Some other meta
					return attrStr;
				}
			);
		}

		// No "charset" attr or "content" attr found... return as-is
		return metaStartStr + metaAttrsStr2 + metaEndStr;
	});

	return {
		html: html2,
		replaced,
		previous: current,
	};
}

export function replaceOrInsertHtmlMetaCharset(
	html: string,
	charset: string
): {
	html: string;
	replaced: boolean;
	inserted: boolean;
	previous: string | undefined;
} {
	const result1 = replaceHtmlMetaCharset(html, charset);
	if (result1.replaced) {
		return {
			...result1,
			inserted: false,
		};
	}

	let inserted = false;
	const html2 = html.replace(rxHead, match => {
		inserted = true;
		return match + '<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />';
	});

	return {
		html: html2,
		replaced: false,
		inserted: inserted,
		previous: undefined,
	};
}
